import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowDownRight, ArrowUpRight, History, Minus } from 'lucide-react';
import api from '@/lib/api';
import { CABECALHO, CELULA, LINHA, TITULO_COLUNA } from './tabela';

type Tonelada = { data_lme: string; data_dolar: string; usd_t: number; dolar: number; brl_t: number };

type Candidata = {
  id: number;
  numero: number;
  data: string;
  dias: number;
  cabos_em_comum: number;
};

type LinhaComparada = {
  produto_id: number;
  produto: string;
  quantidade_m: number;
  kg_km: number | null;
  estimado: boolean;
  preco_la: number;
  preco_agora: number;
  variacao: number;
  so_pelo_cobre: number | null;
  diferenca_m: number | null;
  diferenca_rs: number | null;
};

type Comparacao = {
  anterior: { id: number; numero: number; data: string; dias: number };
  atual: { data: string };
  cobre_la: Tonelada | null;
  cobre_agora: Tonelada | null;
  variacao: { brl_t: number; lme: number; dolar: number } | null;
  totais: {
    valor_la: number;
    valor_agora: number;
    variacao_preco: number | null;
    variacao_so_cobre: number | null;
    diferenca_rs: number | null;
  };
  linhas: LinhaComparada[];
  fora_da_conta_do_cobre: string[];
  estimado: boolean;
};

const reais = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const reaisCurto = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const preco = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const dolarTonelada = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
const cotacaoDolar = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4 });

const pct = (v: number, casas = 2) =>
  `${(v * 100).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`;
const comSinal = (v: number) => `${v > 0 ? '+' : ''}${pct(v)}`;
const dataCurta = (iso: string) => iso.split('-').reverse().join('/');
/** Quanto antes da rodada em análise: "no mesmo dia", "1 dia antes", "14 dias antes". */
const antes = (dias: number) => (dias === 0 ? 'no mesmo dia' : `${dias} ${dias === 1 ? 'dia' : 'dias'} antes`);

/** Abaixo de meio centésimo de ponto percentual, é "estável". */
const ESTAVEL = 0.00005;
const tom = (v: number | null) =>
  v === null || Math.abs(v) < ESTAVEL ? 'text-muted' : v < 0 ? 'text-success' : 'text-danger';
const mexeu = (v: number) => (Math.abs(v) < ESTAVEL ? 'ficou estável' : v < 0 ? `caiu ${pct(Math.abs(v))}` : `subiu ${pct(v)}`);

const TABELA_INICIAL = 10;

/**
 * A compra anterior como régua: "da última vez o cobre estava mais caro e o
 * preço era esse — por que agora não baixou?".
 *
 * O card do cobre compara as propostas com hoje; esta seção compara a rodada
 * em análise com uma cotação anterior fechada — só uma compra que foi até o fim
 * serve de régua. Para cada cabo em comum, o preço de agora contra o que o cobre
 * justificaria. Carrega à parte: a análise aparece sem esperar por ela.
 *
 * Sem cotação fechada antes desta — o começo do uso do sistema — a seção não
 * aparece; ela surge sozinha quando a primeira compra for fechada.
 */
export default function SecaoCotacaoAnterior({ rodadaId, excluidos }: { rodadaId: number; excluidos: number[] }) {
  const [anteriorId, setAnteriorId] = useState<number | null>(null);
  const [todas, setTodas] = useState(false);

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['comparacao-anterior', rodadaId, anteriorId, excluidos],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (anteriorId !== null) params.set('anterior', String(anteriorId));
      excluidos.forEach((id) => params.append('excluir[]', String(id)));

      return (
        await api.get<{ data: { candidatas: Candidata[]; comparacao: Comparacao | null } }>(
          `/rodadas/${rodadaId}/comparacao-anterior?${params.toString()}`,
        )
      ).data.data;
    },
  });

  // Enquanto carrega, nada: se não houver cotação fechada antes, a seção nem
  // chega a aparecer — um aviso "buscando" que some depois seria só ruído.
  if (isLoading) return null;
  if (isError || !data) return <p className="mb-lg text-[13px] text-muted">Não foi possível comparar com as cotações anteriores agora.</p>;
  if (!data.comparacao) return null;

  const c = data.comparacao;
  const linhas = todas ? c.linhas : c.linhas.slice(0, TABELA_INICIAL);

  return (
    <section className={`mb-lg overflow-hidden rounded-lg border border-border bg-surface ${isFetching ? 'opacity-70' : ''}`}>
      <div className="flex flex-wrap items-center justify-between gap-sm border-b border-border px-md py-sm">
        <h2 className="flex items-center gap-xs text-[14px] font-bold text-ink">
          <History size={16} className="text-accent" aria-hidden="true" />
          Contra uma cotação anterior
        </h2>
        <label className="flex items-center gap-xs text-[12px] text-muted">
          Comparar com
          <select
            value={anteriorId ?? c.anterior.id}
            onChange={(evento) => {
              setAnteriorId(Number(evento.target.value));
              setTodas(false);
            }}
            className="max-w-[420px] rounded-md border border-border bg-surface px-sm py-xs text-[13px] text-ink"
          >
            {data.candidatas.map((k) => (
              <option key={k.id} value={k.id}>
                {`Nº ${k.numero} · ${dataCurta(k.data)} · ${antes(k.dias)} · ${k.cabos_em_comum} ${k.cabos_em_comum === 1 ? 'cabo' : 'cabos'} em comum`}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="px-md py-md">
        <Frase c={c} />

        <dl className="mt-md grid gap-sm sm:grid-cols-3">
          <Quadro rotulo={`Na cotação nº ${c.anterior.numero} · ${dataCurta(c.anterior.data)}`} t={c.cobre_la} />
          <Quadro rotulo={`Nesta cotação · ${dataCurta(c.atual.data)}`} t={c.cobre_agora} />
          <div
            className={`rounded-md px-sm py-sm ${
              c.variacao === null || Math.abs(c.variacao.brl_t) < ESTAVEL
                ? 'bg-dominant'
                : c.variacao.brl_t < 0
                  ? 'bg-success/10'
                  : 'bg-danger/10'
            }`}
          >
            <dt className="text-[11px] text-muted">Variação do cobre</dt>
            {c.variacao === null ? (
              <dd className="text-[16px] font-bold text-muted">—</dd>
            ) : (
              <>
                <dd className={`flex items-center gap-[2px] text-[16px] font-bold tabular-nums ${tom(c.variacao.brl_t)}`}>
                  <Seta valor={c.variacao.brl_t} />
                  {comSinal(c.variacao.brl_t)}
                </dd>
                <dd className="text-[11px] tabular-nums text-muted">
                  LME {comSinal(c.variacao.lme)} · dólar {comSinal(c.variacao.dolar)}
                </dd>
              </>
            )}
          </div>
        </dl>
      </div>

      {c.linhas.length > 0 && (
        <div className="overflow-x-auto border-t border-border">
          <table className="w-full text-[13px]">
            <thead>
              <tr className={CABECALHO}>
                <th className={`${TITULO_COLUNA} text-left`}>Cabos em comum</th>
                <th className={`${TITULO_COLUNA} text-right`}>Fechado na nº {c.anterior.numero}</th>
                <th className={`${TITULO_COLUNA} text-right`}>Melhor agora</th>
                <th className={`${TITULO_COLUNA} text-right`}>Variação</th>
                <th className={`${TITULO_COLUNA} text-right`}>Só pelo cobre</th>
                <th className={`${TITULO_COLUNA} text-right`}>Diferença</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => (
                <tr key={l.produto_id} className={LINHA}>
                  <td className={`${CELULA} text-ink`}>{l.produto}</td>
                  <td className={`${CELULA} text-right tabular-nums`}>{preco.format(l.preco_la)}</td>
                  <td className={`${CELULA} text-right tabular-nums`}>{preco.format(l.preco_agora)}</td>
                  <td className={`${CELULA} text-right font-bold tabular-nums ${tom(l.variacao)}`}>{comSinal(l.variacao)}</td>
                  <td className={`${CELULA} text-right tabular-nums text-muted`}>
                    {l.so_pelo_cobre === null ? '—' : `${comSinal(l.so_pelo_cobre)}${l.estimado ? '*' : ''}`}
                  </td>
                  <td className={`${CELULA} whitespace-nowrap text-right tabular-nums ${tom(l.diferenca_rs)}`}>
                    {l.diferenca_m === null || l.diferenca_rs === null ? (
                      '—'
                    ) : (
                      <>
                        <b>{reais.format(l.diferenca_rs)}</b>
                        <span className="block text-[11px]">
                          {l.diferenca_m > 0 ? '+' : ''}
                          {preco.format(l.diferenca_m)}/m
                        </span>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {c.linhas.length > TABELA_INICIAL && (
            <button
              type="button"
              onClick={() => setTodas((v) => !v)}
              className="w-full border-t border-border px-md py-xs text-[12px] font-bold text-accent hover:underline"
            >
              {todas ? `Mostrar só os ${TABELA_INICIAL} primeiros` : `Ver todos os ${c.linhas.length} cabos`}
            </button>
          )}
        </div>
      )}

      <p className="border-t border-border px-md py-sm text-[11px] leading-snug text-muted">
        Na cotação nº {c.anterior.numero} vale o preço fechado, o que foi pago. Agora vale o melhor preço desta rodada. "Só pelo cobre" é quanto o preço mudaria se só o cobre mexesse (peso de
        cobre × tonelada em reais, LME × dólar PTAX).
        {c.estimado && ' * Peso de cobre estimado pela seção nominal.'}
        {c.fora_da_conta_do_cobre.length > 0 && ` Fora da conta do cobre, sem seção: ${c.fora_da_conta_do_cobre.join(', ')}.`}
      </p>
    </section>
  );
}

/** A frase que o Marcelo leva ao telefone. */
function Frase({ c }: { c: Comparacao }) {
  const t = c.totais;
  const cabos = c.linhas.length;
  const quando = `${dataCurta(c.anterior.data).slice(0, 5)}, ${antes(c.anterior.dias)}`;

  if (cabos === 0) {
    return (
      <p className="text-[15px] leading-relaxed text-ink">
        A cotação <b>nº {c.anterior.numero}</b> ({quando}) não tem preço nos mesmos cabos desta rodada.
      </p>
    );
  }

  const acompanhou = t.diferenca_rs !== null && Math.abs(t.diferenca_rs) <= 0.005 * t.valor_la;

  return (
    <p className="text-[15px] leading-relaxed text-ink">
      Desde a cotação <b>nº {c.anterior.numero}</b> ({quando}),{' '}
      {c.variacao ? (
        Math.abs(c.variacao.brl_t) < ESTAVEL && Math.abs(c.variacao.lme) < ESTAVEL && Math.abs(c.variacao.dolar) < ESTAVEL ? (
          <>
            a tonelada de cobre em reais <b className={tom(c.variacao.brl_t)}>{mexeu(c.variacao.brl_t)}</b>.
          </>
        ) : (
          <>
            a tonelada de cobre em reais <b className={tom(c.variacao.brl_t)}>{mexeu(c.variacao.brl_t)}</b> — a LME{' '}
            {mexeu(c.variacao.lme)} e o dólar {mexeu(c.variacao.dolar)}.
          </>
        )
      ) : (
        <>falta o índice do cobre numa das datas, e a comparação fica só nos preços.</>
      )}{' '}
      Nos <b>{cabos} {cabos === 1 ? 'cabo' : 'cabos'} em comum</b>, o melhor preço de agora{' '}
      {t.variacao_preco === null || Math.abs(t.variacao_preco) < ESTAVEL ? (
        <b>está igual</b>
      ) : (
        <b className={tom(t.variacao_preco)}>
          está {pct(Math.abs(t.variacao_preco))} {t.variacao_preco > 0 ? 'acima' : 'abaixo'}
        </b>
      )}
      {t.variacao_so_cobre === null || t.diferenca_rs === null ? (
        '.'
      ) : acompanhou ? (
        <>; o preço acompanhou o cobre — não há argumento aqui.</>
      ) : Math.abs(t.variacao_so_cobre) < ESTAVEL ? (
        t.diferenca_rs > 0 ? (
          <>
            ; com o cobre parado, o preço não tinha por que mudar — são cerca de{' '}
            <b className="text-danger">{reaisCurto.format(t.diferenca_rs)} acima</b> da cotação nº {c.anterior.numero}.
          </>
        ) : (
          <>
            ; com o cobre parado, o preço não tinha por que mudar — são cerca de{' '}
            <b className="text-success">{reaisCurto.format(Math.abs(t.diferenca_rs))} abaixo</b> da cotação nº {c.anterior.numero}.
          </>
        )
      ) : t.diferenca_rs > 0 ? (
        <>
          ; só pelo cobre, deveria estar {pct(Math.abs(t.variacao_so_cobre))}{' '}
          {t.variacao_so_cobre < 0 ? 'abaixo' : 'acima'}. São cerca de{' '}
          <b className="text-danger">{reaisCurto.format(t.diferenca_rs)} acima</b> do que o cobre justifica.
        </>
      ) : (
        <>
          ;{' '}
          {t.variacao_so_cobre > 0
            ? t.variacao_preco !== null && t.variacao_preco > ESTAVEL
              ? 'o aumento é menor que o do cobre'
              : 'o preço não subiu com o cobre'
            : 'o preço caiu mais que o cobre'}
          {' — '}
          <b className="text-success">{reaisCurto.format(Math.abs(t.diferenca_rs))} abaixo</b> do que o cobre justificaria.
        </>
      )}
    </p>
  );
}

function Quadro({ rotulo, t }: { rotulo: string; t: Tonelada | null }) {
  return (
    <div className="rounded-md bg-dominant px-sm py-sm">
      <dt className="text-[11px] text-muted">{rotulo}</dt>
      <dd className="text-[16px] font-bold tabular-nums text-ink">{t ? `${reaisCurto.format(t.brl_t)}/t` : '—'}</dd>
      <dd className="text-[11px] tabular-nums text-muted">
        {t ? `${dolarTonelada.format(t.usd_t)} × ${cotacaoDolar.format(t.dolar)}` : 'sem índice nesta data'}
      </dd>
    </div>
  );
}

function Seta({ valor }: { valor: number }) {
  if (Math.abs(valor) < ESTAVEL) return <Minus size={16} aria-hidden="true" />;
  return valor < 0 ? <ArrowDownRight size={16} aria-hidden="true" /> : <ArrowUpRight size={16} aria-hidden="true" />;
}
